import { useState } from 'react'
import { Link } from 'react-router'
import type { PaymentException, PaymentMethod } from '@fitting-room/contracts'
import { DatePicker } from '../../components/admin/DatePicker'
import { PageHeader } from '../../components/admin/PageHeader'
import { usePaymentsReport } from '../../lib/adminQueries'
import { BOUTIQUE_LOCALE, boutiqueDateTime, boutiqueToday } from '../../lib/boutique'
import { formatDay } from '../../lib/format'
import { shiftDay } from '../../lib/months'
import { formatCents } from '../../lib/orders'
import { PAYMENT_KIND_LABEL, PAYMENT_METHOD_LABEL, PAYMENT_METHOD_ORDER, signedAmount } from '../../lib/payments'
import { useStaffToken } from '../../lib/staffToken'

/** The longest range the report reads at once, as the API holds it. */
const MAX_DAYS = 92

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
}

/** What's wrong with the range, before it is asked for; `null` when it can be read. */
function rangeProblem(from: string, to: string): string | null {
  if (from > to) return 'The start date is after the end date.'
  if (daysBetween(from, to) > MAX_DAYS) return `The report reads at most ${MAX_DAYS} days at a time.`
  return null
}

const EXCEPTION_LABEL: Record<PaymentException['type'], string> = {
  voided: 'Voided',
  backdated: 'Back-dated',
}

/**
 * The owner's payment report: tperent's `PaymentsReportTab`. The top half is
 * what came in — by the day the money moved, by method, so the drawer and
 * the Zelle account can be counted against it. The bottom half is who
 * touched the ledger in the range — every void and every entry recorded for
 * an earlier day — the things a second person should see.
 */
export function PaymentsPage() {
  const { token } = useStaffToken()
  const today = boutiqueToday()
  const [from, setFrom] = useState(today)
  const [to, setTo] = useState(today)
  const problem = rangeProblem(from, to)
  const report = usePaymentsReport(token, { from, to }, problem === null)
  const data = problem === null && !report.isError ? report.data : undefined
  const setRange = (f: string, t: string) => {
    setFrom(f)
    setTo(t)
  }

  return (
    <div>
      <PageHeader eyebrow="What came in, and who touched the ledger" title="Payments" />

      <div className="st-card mb-4 flex flex-wrap items-end gap-3 px-4 py-3">
        <div className="flex flex-col gap-1">
          <span className="st-eyebrow">From</span>
          <DatePicker value={from} onChange={setFrom} label="From" max={today} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="st-eyebrow">To</span>
          <DatePicker value={to} onChange={setTo} label="To" max={today} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" className="st-btn-ghost" onClick={() => setRange(today, today)}>
            Today
          </button>
          <button type="button" className="st-btn-ghost" onClick={() => setRange(shiftDay(today, -1), shiftDay(today, -1))}>
            Yesterday
          </button>
          <button type="button" className="st-btn-ghost" onClick={() => setRange(`${today.slice(0, 8)}01`, today)}>
            This month
          </button>
        </div>
      </div>

      {problem ? (
        <p className="st-alert mb-4" role="alert">
          {problem}
        </p>
      ) : null}
      {!problem && report.isPending ? <p className="st-meta">Loading…</p> : null}
      {!problem && report.isError ? (
        <p className="st-alert mb-4" role="alert">
          Couldn&rsquo;t load the payments. Try again in a moment.
        </p>
      ) : null}

      {data ? (
        <div className="space-y-4">
          <p className="st-meta text-xs" data-testid="report-range">
            {data.from === data.to
              ? `Payments on ${formatDay(data.from, BOUTIQUE_LOCALE)}`
              : `Payments from ${formatDay(data.from, BOUTIQUE_LOCALE)} to ${formatDay(data.to, BOUTIQUE_LOCALE)}`}
          </p>

          <section className="st-card px-4 py-3" aria-label="Totals">
            <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4 lg:grid-cols-7">
              {PAYMENT_METHOD_ORDER.map((method) => (
                <div key={method}>
                  <dt className="st-meta text-xs">{PAYMENT_METHOD_LABEL[method]}</dt>
                  <dd className="tabular-nums">{formatCents(data.totals.byMethod[method])}</dd>
                </div>
              ))}
              <div>
                <dt className="st-meta text-xs">Total</dt>
                <dd className="font-medium tabular-nums" data-testid="report-net">
                  {formatCents(data.totals.netMinor)}
                </dd>
              </div>
            </dl>
            <p className="st-meta mt-2 text-xs">
              {data.totals.count === 1 ? '1 payment' : `${data.totals.count} payments`}, refunds taken off the method they
              went back through. Voided payments don&rsquo;t count.
            </p>
          </section>

          {data.byDay.length > 1 ? (
            <section className="st-card px-4 py-3" aria-labelledby="report-by-day">
              <h2 id="report-by-day" className="st-eyebrow mb-2">
                By day
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="st-meta text-left text-xs">
                      <th className="py-1.5 pr-3 font-normal">Day</th>
                      {PAYMENT_METHOD_ORDER.map((method) => (
                        <th key={method} className="py-1.5 pr-3 text-right font-normal">
                          {PAYMENT_METHOD_LABEL[method]}
                        </th>
                      ))}
                      <th className="py-1.5 text-right font-normal">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byDay.map((day) => (
                      <tr key={day.day} className="border-t" style={{ borderColor: 'var(--st-rule)' }}>
                        <td className="whitespace-nowrap py-1.5 pr-3">{formatDay(day.day, BOUTIQUE_LOCALE)}</td>
                        {PAYMENT_METHOD_ORDER.map((method) => (
                          <td key={method} className="py-1.5 pr-3 text-right tabular-nums">
                            {day.byMethod[method] ? formatCents(day.byMethod[method]) : '—'}
                          </td>
                        ))}
                        <td className="py-1.5 text-right font-medium tabular-nums">{formatCents(day.netMinor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <section className="st-card px-4 py-3" aria-labelledby="report-payments">
            <h2 id="report-payments" className="st-eyebrow mb-1">
              Every payment
            </h2>
            {data.payments.length === 0 ? (
              <p className="st-meta py-3 text-sm">No payments in this range.</p>
            ) : (
              <ul className="divide-y divide-[color:var(--st-rule)]">
                {data.payments.map((payment) => {
                  const voided = Boolean(payment.voidedAt)
                  return (
                    <li key={payment.id} className="py-2.5 text-sm" data-testid="report-row">
                      <div className="flex items-start gap-3">
                        <div className={`min-w-0 flex-1 ${voided ? 'st-voided' : ''}`}>
                          <span className="tabular-nums">{formatDay(payment.paidAt, BOUTIQUE_LOCALE)}</span>
                          <span> · </span>
                          <Link to={`/admin/orders/${payment.order.id}`} className="underline decoration-[color:var(--st-rule-strong)] underline-offset-2">
                            {payment.order.orderNo}
                          </Link>
                          <span> {payment.order.customerName}</span>
                          <span className="st-meta">
                            {' '}
                            · {PAYMENT_KIND_LABEL[payment.kind]} · {methodWords(payment.method, payment.reference)}
                          </span>
                        </div>
                        <span className={`shrink-0 font-medium tabular-nums ${voided ? 'st-voided' : ''}`}>{signedAmount(payment)}</span>
                      </div>
                      <div className="st-meta mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs">
                        <span>Recorded by {payment.recordedByLabel}</span>
                        {voided ? <span style={{ color: '#96432f' }}>· Voided</span> : null}
                        {payment.backdated ? <span className="st-badge st-badge--open">Back-dated</span> : null}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <section className="st-card px-4 py-3" aria-labelledby="report-exceptions">
            <div className="mb-1 flex flex-wrap items-baseline gap-x-2">
              <h2 id="report-exceptions" className="st-eyebrow">
                Voids and back-dated entries
              </h2>
              <span className="st-meta text-xs">by the day they were made</span>
            </div>
            {data.exceptions.length === 0 ? (
              <p className="st-meta py-3 text-sm">Nothing voided or back-dated in this range.</p>
            ) : (
              <ul className="divide-y divide-[color:var(--st-rule)]">
                {data.exceptions.map((exception) => (
                  <li key={`${exception.type}-${exception.payment.id}`} className="py-2.5 text-sm" data-testid="exception-row">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className={`st-badge ${exception.type === 'voided' ? 'st-badge--alert' : 'st-badge--open'}`}>
                        {EXCEPTION_LABEL[exception.type]}
                      </span>
                      <span>
                        {PAYMENT_KIND_LABEL[exception.payment.kind]} {signedAmount(exception.payment)} ·{' '}
                        {methodWords(exception.payment.method, exception.payment.reference)}
                        {exception.type === 'backdated' ? `, paid ${formatDay(exception.payment.paidAt, BOUTIQUE_LOCALE)}` : ''}
                      </span>
                      <Link to={`/admin/orders/${exception.order.id}`} className="underline decoration-[color:var(--st-rule-strong)] underline-offset-2">
                        {exception.order.orderNo}
                      </Link>
                      <span className="st-meta">{exception.order.customerName}</span>
                    </div>
                    <div className="st-meta mt-0.5 text-xs">
                      {exception.actorLabel}, {boutiqueDateTime(exception.at)}
                      {exception.reason ? <span style={{ color: 'var(--st-ink)' }}> · Reason: {exception.reason}</span> : null}
                      {exception.note ? <span> · Note: {exception.note}</span> : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </div>
  )
}

function methodWords(method: PaymentMethod, reference: string | undefined): string {
  return reference ? `${PAYMENT_METHOD_LABEL[method]} ${reference}` : PAYMENT_METHOD_LABEL[method]
}
